import { useOrgContext, useOrgEditActions } from '@/org/contexts/OrgContext'
import useOrgBaseRoles from '@/org/hooks/useOrgBaseRoles'
import CircleSearchButton from '@/search/components/CircleSearchButton'
import {
  Box,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { RoleSummaryFragment } from '@gql'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiX } from 'react-icons/fi'
import { CircleLinkIcon, CreateIcon } from 'src/icons'
import BaseRoleSearchButton from '../../search/components/BaseRoleSearchButton'
import { CircleContext } from '../contexts/CIrcleContext'
import CircleLinkDeleteModal from '../modals/CircleLinkDeleteModal'
import CircleWithLeaderItem from './CircleWithLeaderItem'

// Same small-caps rendering as the circle names (ORG-01), applied to the group
// headings.
const groupHeadingStyle = {
  fontVariant: 'small-caps',
  fontFeatureSettings: "'smcp'",
}

export default function CircleRoleSubCircles() {
  const { t } = useTranslation()

  // Get circle context
  const circleContext = useContext(CircleContext)
  if (!circleContext) return null
  const {
    circle,
    participants,
    canEditSubCircles,
    canEditSubCirclesParentLinks,
  } = circleContext

  const { orgData } = useOrgContext()
  const roles = useOrgBaseRoles()
  const { createCircle, addCircleLink } = useOrgEditActions()

  // CircleLinkDeleteModal
  const [linkToDeleteId, setLinkToDeleteId] = useState<string | undefined>()
  const {
    isOpen: isDeleteLinkOpen,
    onOpen: onDeleteLinkOpen,
    onClose: onDeleteLinkClose,
  } = useDisclosure()

  // Get direct circles children, grouped the way the panel lists them
  const groups = useMemo(
    () => orgData?.childrenGroupsOf(circle.id),
    [orgData, circle]
  )

  // Every group feeds the exclusion list: a role already held by the circle
  // must not be offered again when adding one.
  const subRolesIds = useMemo(
    () =>
      groups &&
      [
        ...groups.links,
        ...groups.structural,
        ...groups.subCircles,
        ...groups.functional,
      ].map((c) => c.roleId),
    [groups]
  )

  // An empty group shows no heading: a title standing above nothing reads as a
  // bug.
  const titledGroups = useMemo(
    () =>
      (
        [
          {
            key: 'links',
            title: t('CircleRoleSubCircles.linkRoles'),
            circles: groups?.links ?? [],
          },
          {
            key: 'structural',
            title: t('CircleRoleSubCircles.structuralRoles'),
            circles: groups?.structural ?? [],
          },
          {
            key: 'subCircles',
            title: t('CircleRoleSubCircles.subCircles'),
            circles: groups?.subCircles ?? [],
          },
          {
            key: 'functional',
            title: t('CircleRoleSubCircles.functionalRoles'),
            circles: groups?.functional ?? [],
          },
        ] as const
      ).filter((group) => group.circles.length > 0),
    [groups, t]
  )

  const highlightButton = subRolesIds?.length === 0 && participants.length === 0

  // Create sub-circle (and its role if a name is given)
  const handleCreateCircle = useCallback(
    (roleOrName: RoleSummaryFragment | string) =>
      createCircle(circle.id, roleOrName),
    [circle, createCircle]
  )

  const handleAddRole = useCallback(
    async (roleId: string) => {
      const role = roles?.find((r) => r.id === roleId)
      if (!role) return
      handleCreateCircle(role)
    },
    [handleCreateCircle, roles]
  )

  // When the user only leads the parent circle (not this one), they can only
  // add a representative (parent-link) sub-circle, not create roles here.
  const onlyParentLink = canEditSubCirclesParentLinks && !canEditSubCircles

  // Get invited circles (links)
  const invitedCircles = useMemo(
    () =>
      orgData
        ? [...orgData.invitedCirclesOf(circle.id)].sort((a, b) =>
            (orgData.getRole(a.roleId)?.name ?? '').localeCompare(
              orgData.getRole(b.roleId)?.name ?? ''
            )
          )
        : undefined,
    [orgData, circle]
  )

  // List of circles ids to exclude from circle search when adding a link
  const excludedCirclesIds = useMemo(() => {
    // Exclude current circle
    const ids = [circle.id]
    // Exclude parent circle
    if (circle.parentId) ids.push(circle.parentId)
    // Exclude already invited circle
    if (invitedCircles) ids.push(...invitedCircles.map((c) => c.id))
    // Exclude children circles
    const children = orgData?.circles.filter((c) => c.parentId === circle.id)
    if (children) ids.push(...children.map((c) => c.id))
    return ids
  }, [circle, invitedCircles, orgData])

  const handleAddLink = useCallback(
    async (circleId: string) => {
      addCircleLink(circle.id, circleId)
    },
    [circle]
  )

  const handleDeleteLink = useCallback((circleId: string) => {
    setLinkToDeleteId(circleId)
    onDeleteLinkOpen()
  }, [])

  // Hide if read only and empty
  if (
    !canEditSubCircles &&
    !canEditSubCirclesParentLinks &&
    !subRolesIds?.length &&
    !invitedCircles?.length
  ) {
    return null
  }

  return (
    <Box>
      {isDeleteLinkOpen && linkToDeleteId && (
        <CircleLinkDeleteModal
          parentId={circle.id}
          circleId={linkToDeleteId}
          isOpen
          onClose={onDeleteLinkClose}
        />
      )}

      <Heading as="h3" size="sm" mb={3}>
        {t('CircleRoleSubCircles.roles')}
      </Heading>
      <VStack spacing={4} align="start">
        {titledGroups.map((group, index) => (
          <Box key={group.key} w="100%">
            {index > 0 && <Divider mb={4} />}
            <Heading
              as="h4"
              size="xs"
              mb={2}
              color="gray.500"
              sx={groupHeadingStyle}
            >
              {group.title}
            </Heading>
            <VStack spacing={2} align="start">
              {group.circles.map((subCircle) => (
                <CircleWithLeaderItem
                  key={subCircle.id}
                  circle={subCircle}
                  parentCircle={circle}
                  participants={participants}
                  showAddMember
                />
              ))}
            </VStack>
          </Box>
        ))}

        {invitedCircles && invitedCircles.length > 0 && (
          <Box w="100%">
            {titledGroups.length > 0 && <Divider mb={4} />}
            <Heading
              as="h4"
              size="xs"
              mb={2}
              color="gray.500"
              sx={groupHeadingStyle}
            >
              {t('CircleRoleSubCircles.invitedRoles')}
            </Heading>
            <VStack spacing={2} align="start">
              {invitedCircles.map((invitedCircle) => (
                <Flex key={invitedCircle.id} alignItems="center">
                  <CircleWithLeaderItem
                    circle={invitedCircle}
                    parentCircle={circle}
                    participants={participants}
                    isInvited
                  />
                  <Tooltip
                    label={t('CircleRoleSubCircles.linkTooltip')}
                    placement="top"
                    hasArrow
                  >
                    <Icon as={CircleLinkIcon} ml={2} />
                  </Tooltip>
                  {canEditSubCircles && (
                    <Tooltip
                      label={t('CircleRoleSubCircles.removeLink')}
                      placement="top"
                      hasArrow
                    >
                      <IconButton
                        aria-label={t('common.remove')}
                        icon={<FiX />}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleDeleteLink(invitedCircle.id)
                        }}
                      />
                    </Tooltip>
                  )}
                </Flex>
              ))}
            </VStack>
          </Box>
        )}

        <HStack>
          {(canEditSubCircles || canEditSubCirclesParentLinks) && (
            <BaseRoleSearchButton
              excludeIds={subRolesIds}
              size="sm"
              variant={highlightButton ? 'solid' : 'outline'}
              colorScheme={highlightButton ? 'blue' : undefined}
              borderRadius="full"
              parentLink={
                onlyParentLink
                  ? true
                  : !canEditSubCirclesParentLinks && canEditSubCircles
                    ? false
                    : undefined
              }
              leftIcon={<CreateIcon size={20} />}
              onSelect={handleAddRole}
              onCreate={canEditSubCircles ? handleCreateCircle : undefined}
            >
              {t(
                onlyParentLink
                  ? 'CircleRoleSubCircles.addRepresentative'
                  : 'CircleRoleSubCircles.addRole'
              )}
            </BaseRoleSearchButton>
          )}

          {canEditSubCircles && (
            <CircleSearchButton
              excludeIds={excludedCirclesIds}
              defaultPerimeter="myCircles"
              size="sm"
              variant="outline"
              borderRadius="full"
              leftIcon={<CircleLinkIcon size={20} />}
              onSelect={handleAddLink}
            >
              {t('CircleRoleSubCircles.inviteRole')}
            </CircleSearchButton>
          )}
        </HStack>
      </VStack>
    </Box>
  )
}
